import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Plus, Edit2, Trash2, GripVertical, CalendarDays, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TYPE_COLORS = [
    { name: 'Stone', value: '#78716c' },
    { name: 'Red', value: '#dc2626' },
    { name: 'Orange', value: '#ea580c' },
    { name: 'Amber', value: '#d97706' },
    { name: 'Green', value: '#16a34a' },
    { name: 'Teal', value: '#0d9488' },
    { name: 'Blue', value: '#2563eb' },
    { name: 'Indigo', value: '#4f46e5' },
    { name: 'Purple', value: '#9333ea' },
    { name: 'Pink', value: '#db2777' },
];

const EMPTY_FORM = { label: '', description: '', color: '#78716c' };

export default function EventTypeAdmin() {
    const { getAuthHeader } = useAuth();
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Inline add/edit state
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const loadTypes = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/event-types`, {
                headers: getAuthHeader(),
            });
            const data = res.data?.types || res.data || [];
            setTypes(data);
        } catch {
            toast.error('Failed to load event types');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => {
        loadTypes();
    }, [loadTypes]);

    // ── Create ──────────────────────────────────────────────────────────

    const handleAdd = async () => {
        if (!addForm.label.trim()) {
            toast.error('Label is required');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/admin/event-types`, {
                label: addForm.label.trim(),
                description: addForm.description.trim(),
                color: addForm.color,
            }, { headers: getAuthHeader() });
            toast.success('Event type created');
            setShowAdd(false);
            setAddForm(EMPTY_FORM);
            loadTypes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create event type');
        } finally {
            setSaving(false);
        }
    };

    // ── Edit ────────────────────────────────────────────────────────────

    const startEdit = (type) => {
        setEditingId(type.type_id || type._id);
        setEditForm({
            label: type.label || '',
            description: type.description || '',
            color: type.color || '#78716c',
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm(EMPTY_FORM);
    };

    const handleUpdate = async (typeId) => {
        if (!editForm.label.trim()) {
            toast.error('Label is required');
            return;
        }
        setSaving(true);
        try {
            await axios.put(`${API}/api/admin/event-types/${typeId}`, {
                label: editForm.label.trim(),
                description: editForm.description.trim(),
                color: editForm.color,
            }, { headers: getAuthHeader() });
            toast.success('Event type updated');
            setEditingId(null);
            setEditForm(EMPTY_FORM);
            loadTypes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update');
        } finally {
            setSaving(false);
        }
    };

    // ── Delete (deactivate) ─────────────────────────────────────────────

    const handleDelete = async (typeId) => {
        if (!window.confirm('Deactivate this event type? It will no longer appear as an option for new events.')) return;
        setDeletingId(typeId);
        try {
            await axios.delete(`${API}/api/admin/event-types/${typeId}`, {
                headers: getAuthHeader(),
            });
            toast.success('Event type deactivated');
            loadTypes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to deactivate');
        } finally {
            setDeletingId(null);
        }
    };

    // ── Reorder ─────────────────────────────────────────────────────────

    const moveType = async (index, direction) => {
        const newTypes = [...types];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newTypes.length) return;
        [newTypes[index], newTypes[targetIndex]] = [newTypes[targetIndex], newTypes[index]];
        setTypes(newTypes);

        // Persist order — send the reordered IDs
        try {
            const orderedIds = newTypes.map(t => t.type_id || t._id);
            await axios.put(`${API}/api/admin/event-types`, {
                order: orderedIds,
            }, { headers: getAuthHeader() });
        } catch {
            toast.error('Failed to save order');
            loadTypes();
        }
    };

    // ── Inline form component ───────────────────────────────────────────

    const InlineForm = ({ form, setForm, onConfirm, onCancel: onCancelForm, confirmLabel }) => (
        <div className="p-4 border border-stone-200 rounded-lg bg-white space-y-3">
            <div className="space-y-1.5">
                <label className="text-sm font-medium text-stone-700">Label <span className="text-red-500">*</span></label>
                <input
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="e.g. Webinar, Trade Show, Workshop"
                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                    autoFocus
                />
            </div>
            <div className="space-y-1.5">
                <label className="text-sm font-medium text-stone-700">Description</label>
                <input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Optional description"
                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                />
            </div>
            <div className="space-y-1.5">
                <label className="text-sm font-medium text-stone-700">Color</label>
                <div className="flex flex-wrap gap-2">
                    {TYPE_COLORS.map((c) => (
                        <button
                            key={c.value}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, color: c.value }))}
                            className={`w-7 h-7 rounded-full border-2 transition-all ${
                                form.color === c.value
                                    ? 'border-stone-800 scale-110'
                                    : 'border-transparent hover:border-stone-300'
                            }`}
                            style={{ backgroundColor: c.value }}
                            title={c.name}
                        />
                    ))}
                </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
                <Button size="sm" onClick={onConfirm} disabled={saving}>
                    <Check className="w-3.5 h-3.5 mr-1" />
                    {confirmLabel}
                </Button>
                <Button size="sm" variant="outline" onClick={onCancelForm}>
                    <X className="w-3.5 h-3.5 mr-1" />
                    Cancel
                </Button>
            </div>
        </div>
    );

    // ── Render ──────────────────────────────────────────────────────────

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-stone-500" />
                    <h3 className="text-base font-semibold text-stone-800">Event Types</h3>
                    <span className="text-xs text-stone-400 ml-1">
                        {types.length} type{types.length !== 1 ? 's' : ''}
                    </span>
                </div>
                {!showAdd && (
                    <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add Event Type
                    </Button>
                )}
            </div>

            {/* Add Form */}
            {showAdd && (
                <InlineForm
                    form={addForm}
                    setForm={setAddForm}
                    onConfirm={handleAdd}
                    onCancel={() => { setShowAdd(false); setAddForm(EMPTY_FORM); }}
                    confirmLabel="Create"
                />
            )}

            {/* Types List */}
            {loading ? (
                <div className="text-sm text-stone-400 py-8 text-center">Loading event types...</div>
            ) : types.length === 0 ? (
                <div className="text-sm text-stone-400 py-8 text-center border border-dashed border-stone-200 rounded-lg">
                    No event types defined yet. Click "Add Event Type" to get started.
                </div>
            ) : (
                <div className="space-y-2">
                    {types.map((type, index) => {
                        const typeId = type.type_id || type._id;
                        const isEditing = editingId === typeId;
                        const isDefault = type.is_default === true;
                        const isActive = type.active !== false;

                        if (isEditing) {
                            return (
                                <InlineForm
                                    key={typeId}
                                    form={editForm}
                                    setForm={setEditForm}
                                    onConfirm={() => handleUpdate(typeId)}
                                    onCancel={cancelEdit}
                                    confirmLabel="Save"
                                />
                            );
                        }

                        return (
                            <div
                                key={typeId}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                                    isActive
                                        ? 'border-stone-200 bg-white hover:bg-stone-50'
                                        : 'border-stone-100 bg-stone-50 opacity-60'
                                }`}
                            >
                                {/* Reorder grip */}
                                <div className="flex flex-col gap-0.5">
                                    <button
                                        type="button"
                                        onClick={() => moveType(index, -1)}
                                        disabled={index === 0}
                                        className="text-stone-300 hover:text-stone-600 disabled:opacity-30 disabled:cursor-default"
                                        title="Move up"
                                    >
                                        <GripVertical className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Color dot */}
                                <span
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: type.color || '#78716c' }}
                                />

                                {/* Label + description */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-stone-800 truncate">
                                            {type.label}
                                        </span>
                                        {isDefault && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-stone-200 text-stone-600">
                                                Default
                                            </span>
                                        )}
                                        {!isActive && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600">
                                                Inactive
                                            </span>
                                        )}
                                    </div>
                                    {type.description && (
                                        <p className="text-xs text-stone-400 truncate mt-0.5">
                                            {type.description}
                                        </p>
                                    )}
                                </div>

                                {/* Order badge */}
                                <span className="text-[10px] text-stone-300 font-mono w-6 text-center flex-shrink-0">
                                    #{index + 1}
                                </span>

                                {/* Actions */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => startEdit(type)}
                                        className="p-1.5 rounded text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"
                                        title="Edit"
                                    >
                                        <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    {!isDefault && (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(typeId)}
                                            disabled={deletingId === typeId}
                                            className="p-1.5 rounded text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                                            title="Deactivate"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
